import React, { useContext, useEffect, useState } from "react"
import ICamper from "../../../../interfaces/ICamper"
import { EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/24/solid"
import DropdownButton from "../../../ui/Admin/DropdownButton"
import IPaymentMethods from "../../../../interfaces/IPaymentMethods"
import { AdminContext } from "../../../../contexts/AdminContext"
import PaymentMethodModalContent from "../PaymentMethodModalContent"
import api from "../../../../api2"
import Spinner from "../../../ui/Spinner"
import { useDebouncedCallback } from "use-debounce"
import * as Sentry from "@sentry/react"
import PortalHoverTip from "~/components/ui/PortalHoverTip"
import { useNotification } from "~/contexts/NotificationProvider"
import { useConfirm } from "~/hooks/useConfirm"
import { useModal } from "~/contexts/ModalProvider"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "~/constants/queryKeys"

interface ComponentProps {
  camper: ICamper
  setCamper: React.Dispatch<React.SetStateAction<ICamper>>
}

const CamperPaymentMethods = (props: ComponentProps) => {
  const { showNotification } = useNotification()
  const { openModal, closeModal } = useModal()
  const { showConfirm, ConfirmModal } = useConfirm()
  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)

  const isDisabled = !props?.camper?.email && !props?.camper?.phone

  const refreshPaymentMethods = useDebouncedCallback(async () => {
    setIsLoading(true)
    queryClient.invalidateQueries({ queryKey: [queryKeys.CAMPER] })

    setIsLoading(false)
  }, 100)

  const handleClickAddNewPaymentMethod = () => {
    openModal("generic", {
      children: <PaymentMethodModalContent camper={props?.camper} />,
      className: "max-w-3xl",
      onClose: () => {
        closeModal()

        refreshPaymentMethods()
      },
    })
  }

  const handleClickRemovePaymentMethod = async (paymentMethod: IPaymentMethods) => {
    setIsLoading(true)

    const isConfirmed = await showConfirm({
      title: "Are you sure you want to delete this payment method?",
      description:
        paymentMethod.brand && paymentMethod.last4
          ? paymentMethod.brand.toUpperCase() + " ending in " + paymentMethod.last4
          : "",
      ctaLabel: "Remove",
      colorName: "red",
      ctaColorName: "red",
    })

    if (isConfirmed) {
      try {
        await api.Campers.removeCreditCard(props?.camper?.id, paymentMethod.id)
        queryClient.invalidateQueries({ queryKey: [queryKeys.CAMPER] })
      } catch (err) {
        showNotification({
          type: "error",
          title: "Failed to remove credit card",
          description: err.message,
          dismissAfter: 5000,
        })
      }
    }

    setIsLoading(false)
  }

  return (
    <div className="relative" data-testid="payment-methods-section">
      <ConfirmModal />
      <label className="block text-lg font-semibold text-gray-900 mb-1">Saved payment methods</label>
      <div className="gap-2 mb-2 mt-1 w-full flex flex-col">
        {isLoading && (
          <div className="absolute w-full h-full flex items-center justify-center bg-white opacity-50 z-10">
            <Spinner size={5} />
          </div>
        )}
        {props.camper?.payment_methods?.length ? (
          props.camper?.payment_methods.map((paymentMethod, index) => (
            <div key={index} className="py-1 rounded-md flex items-center justify-between" >
              <div className="flex items-center">
                <div className="mr-3 bg-gray-300 text-gray-400 text-xs font-bold w-14 h-14 rounded-md flex items-center justify-center">
                  {paymentMethod.exp_year ? "CARD" : "BANK"}
                </div>
                <div className="flex flex-col">
                  <div className="text-sm font-semibold text-gray-400">{paymentMethod.brand?.toUpperCase()}</div>
                  <div className="flex items-center text-gray-800">
                    <div className="mr-12 whitespace-nowrap">**** {paymentMethod.last4}</div>
                    <div className="">
                      {paymentMethod.exp_month && paymentMethod.exp_year && (
                        <>
                          {paymentMethod.exp_month}/{paymentMethod.exp_year}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="" data-testid="payment-method-menu-button">
                <DropdownButton
                  options={[
                    {
                      label: "Remove",
                      onClick: (e) => handleClickRemovePaymentMethod(paymentMethod),
                    },
                  ]}
                >
                  <EllipsisHorizontalIcon className="rounded-md p-2 w-10 h-10 transition-colors duration-100 hover:bg-gray-100 text-gray-600 cursor-pointer" />
                </DropdownButton>
              </div>
            </div>
          ))
        ) : (
          <div className="text-gray-600 text-sm">No payment methods saved</div>
        )}
        <a
          onClick={!props.camper?.email && !props.camper?.phone_number ? undefined : handleClickAddNewPaymentMethod}
          className={`mb-4 text-blue-500 hover:text-blue-600 hover:underline cursor-pointer inline-flex items-center ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <PortalHoverTip
            content="Please add either an email or phone number to save payment methods"
            placement="right"
            active={isDisabled}
          >
            <div className="flex items-center">
              <PlusIcon className="h-6 w-6 mr-1" />
              Add new payment method
            </div>
          </PortalHoverTip>
        </a>
      </div>
    </div>
  )
}

export default CamperPaymentMethods
